'use client';

import * as Styled from './InputField.styled';
import { Typography } from '../Typography/Typography';
import {
  ChangeEvent,
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

export type TInputFieldStylesApplied = 'loginInput' | 'none';

interface IInputField {
  placeholder?: string;
  type: 'email' | 'password' | 'text';
  stylesApplied?: TInputFieldStylesApplied;
  animatePlaceHolder?: boolean;
  ariaLabel?: string;
  ref?: Ref<HTMLInputElement>;
  name?: string;
  callbackOnChange?: (e: ChangeEvent<HTMLInputElement>) => void;
}

export interface InputFieldRef {
  updateInputValue: (newValue: string) => void;
  getInputValue: () => string;
}

export const InputField = forwardRef<InputFieldRef, IInputField>(
  (
    {
      placeholder,
      type,
      stylesApplied = 'none',
      animatePlaceHolder = false,
      name,
      ariaLabel,
      callbackOnChange,
    },
    ref
  ) => {
    const [value, setValue] = useState('');
    const [isEmpty, setIsEmpty] = useState(true);

    const refInput = useRef<HTMLInputElement>(null);

    const updateInputValue = (newValue: string) => {
      setTimeout(() => {
        setValue(newValue);
        if (refInput.current) {
          refInput.current.value = newValue;
        }
      }, 300);
    };

    const getInputValue = () => {
      return value;
    };

    useImperativeHandle(ref, () => ({
      updateInputValue,
      getInputValue,
    }));

    useEffect(() => {
      setIsEmpty(value === '');
    }, [value]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
      callbackOnChange?.(e);
    };

    return (
      <Styled.InputContainer stylesApplied={stylesApplied}>
        <Styled.FormInput
          ref={refInput}
          stylesApplied={stylesApplied}
          isEmpty={isEmpty}
          type={type}
          name={name || type}
          value={value}
          onChange={handleInputChange}
          aria-label={ariaLabel || placeholder || type}
          {...(!animatePlaceHolder && { placeholder })}
        />
        {animatePlaceHolder && (
          <Typography
            text={placeholder}
            color="tertiary"
            fontFamily="helvetica-light"
          />
        )}
      </Styled.InputContainer>
    );
  }
);

InputField.displayName = 'InputField';
